import React, { useEffect, useState } from "react";
import { SwipeableDrawer, Autocomplete, TextField, CircularProgress } from "@mui/material";
import { Select, Option, Modal, ModalDialog } from "@mui/joy";
import { Close, Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../fetch/fetch";
import styles from "./faqadmin.module.css";
import { formatFullDateTime, toastOptions } from "./faqutil";
import FaqCategoryModal from "./faqcategorymodal";

/**
 * FAQ 편집 드로어 (목업 AdminFaq 우측 520px) — 신규/수정 공용
 *  props: open, mode('edit'|'new'), itemId, prefill{question,category}, feedbackId, categories, onClose, onSaved(id, mode), onDeleted, onCategoriesChanged, goReview
 *  - 카테고리: Joy Select(faq_category 정렬순) + (+) → 카테고리 관리 모달. 추가하면 바로 선택
 *  - 세부분류: MUI Autocomplete freeSolo (해당 카테고리의 기존 세부분류 자동완성, 새 값 허용)
 *  - 저장 라벨 "저장 (배포 전까지 미반영)". feedbackId 가 있으면 서버가 그 피드백을 resolved 처리
 */
const FaqEditDrawer : React.FC<any> = (props : any) => {

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [item, setItem] = useState<any>(null);
    const [feedbackCount, setFeedbackCount] = useState(0);
    const [category, setCategory] = useState("");
    const [subcategory, setSubcategory] = useState("");
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [note, setNote] = useState("");
    const [source, setSource] = useState("");
    const [catModalOpen, setCatModalOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!props.open) {
            return;
        }
        setConfirmDelete(false);
        if (props.mode === "edit" && props.itemId) {
            setLoading(true);
            setItem(null);
            getFetch(`/api/faq/items/${props.itemId}`).then((result : any) => {
                if (result && result.message === "success") {
                    const it = result.item;
                    setItem(it);
                    setFeedbackCount(+result.feedbackCount);
                    setCategory(it.category || "");
                    setSubcategory(it.subcategory || "");
                    setQuestion(it.question || "");
                    setAnswer(it.answer || "");
                    setNote(it.note || "");
                    setSource(it.source || "");
                } else {
                    toast.error("항목을 불러오지 못했습니다", toastOptions);
                    props.onClose();
                }
            }).catch((e : any) => {
                console.log(e);
                props.onClose();
            }).finally(() => setLoading(false));
        } else {
            setItem(null);
            setFeedbackCount(0);
            const p = props.prefill || {};
            setCategory(p.category && props.categories.some((c : any) => c.name === p.category) ? p.category : "");
            setSubcategory("");
            setQuestion(p.question || "");
            setAnswer("");
            setNote("");
            setSource(props.feedbackId ? "검토 큐" : "");
        }
    }, [props.open, props.mode, props.itemId]);

    const currentCat = props.categories.find((c : any) => c.name === category);
    const subOptions : string[] = currentCat ? currentCat.subcategories : [];

    const save = async () => {
        if (!category) {
            toast.warning("카테고리를 선택해 주세요", toastOptions);
            return;
        }
        if (!question.trim() || !answer.trim()) {
            toast.warning("질문과 답변은 비울 수 없습니다", toastOptions);
            return;
        }
        setSaving(true);
        try {
            const body : any = { category, subcategory : subcategory.trim(), question : question.trim(), answer : answer.replace(/\r\n/g, "\n").trim(), note : note.trim(), source : source.trim() };
            if (props.feedbackId) {
                body.feedbackId = props.feedbackId;
            }
            const url = props.mode === "edit" ? `/api/faq/items/${props.itemId}/update` : "/api/faq/items";
            const result : any = await postFetch(url, body);
            if (result && result.message === "success") {
                toast.success(props.mode === "edit" ? `#${result.id} 저장됨 (배포 전까지 미반영)` : `#${result.id} 추가됨 (배포 전까지 미반영)`, toastOptions);
                props.onSaved(result.id, props.mode);
            } else if (result && result.message === "wrongCategory") {
                toast.error("카테고리 목록에 없는 값입니다", toastOptions);
            } else {
                toast.error("저장에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("저장에 실패했습니다", toastOptions);
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!props.itemId) {
            return;
        }
        try {
            const result : any = await postFetch(`/api/faq/items/${props.itemId}/delete`, {});
            if (result && result.message === "success") {
                toast.success(`#${props.itemId} 삭제됨 (배포 전까지 직원 화면에는 그대로 나갑니다)`, toastOptions);
                props.onDeleted();
            } else {
                toast.error("삭제에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("삭제에 실패했습니다", toastOptions);
        }
    };

    const isEdit = props.mode === "edit";

    return (
        <SwipeableDrawer anchor="right" open={props.open} onClose={props.onClose} onOpen={() => {}} disableSwipeToOpen>
            <div className={styles.drawer} role="presentation">
                <div className={styles.drawerHeader}>
                    <div className={styles.drawerTitle}>
                        {isEdit ? `FAQ #${props.itemId} 수정` : "새 FAQ"}
                        {item && item.unreleased ? <span className={`${styles.badge} ${styles.badgeAmber} ${styles.badgeSm}`}>미배포</span> : null}
                        {!isEdit && props.feedbackId ? <span className={`${styles.badge} ${styles.badgeInfo} ${styles.badgeSm}`}>검토 큐에서</span> : null}
                    </div>
                    <button className={styles.closeBtn} aria-label="닫기" onClick={props.onClose}><Close /></button>
                </div>

                <div className={styles.drawerBody}>
                    {
                        loading ?
                            <div className={styles.rowFlex} style={{ color : "#6B7280", fontSize : "13px" }}><CircularProgress size={16} /> 불러오는 중…</div>
                            :
                            <>
                                <div className={styles.grid2}>
                                    <div className={styles.field}>
                                        <div className={styles.label}>카테고리</div>
                                        <div className={styles.rowFlex}>
                                            <Select size="md" value={category} onChange={(e, v) => { setCategory(v === null ? "" : String(v)); }} sx={{ flex : 1, borderRadius : "8px", height : "40px" }} placeholder="선택">
                                                {props.categories.map((c : any) => <Option key={c.id} value={c.name}>{c.name}</Option>)}
                                            </Select>
                                            <button className={styles.iconBtn} aria-label="카테고리 관리" title="카테고리 관리" onClick={() => setCatModalOpen(true)}><Add sx={{ fontSize : 20 }} /></button>
                                        </div>
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.label}>세부분류 <span>(자동완성, 새 값 가능)</span></div>
                                        <Autocomplete
                                            freeSolo
                                            options={subOptions}
                                            value={subcategory}
                                            inputValue={subcategory}
                                            onInputChange={(e, v) => setSubcategory(v || "")}
                                            onChange={(e, v) => setSubcategory(typeof v === "string" ? v : (v || ""))}
                                            renderInput={(params) => <TextField {...params} size="small" placeholder="예: 결제 시간/마감" sx={{ "& .MuiOutlinedInput-root" : { borderRadius : "8px", height : "40px", fontSize : "14px" } }} />}
                                        />
                                    </div>
                                </div>

                                <div className={styles.field}>
                                    <div className={styles.label}>질문</div>
                                    <textarea className={styles.textarea} rows={2} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="직원이 묻는 말투로. 예: 주말에도 결제 되나요?" />
                                </div>

                                <div className={styles.field}>
                                    <div className={styles.label}>답변 <span>— 직원 화면에 이 원문이 그대로 나갑니다 (줄바꿈 유지)</span></div>
                                    <textarea className={styles.textarea} rows={11} value={answer} onChange={(e) => setAnswer(e.target.value)} />
                                </div>

                                <div className={styles.grid2}>
                                    <div className={styles.field}>
                                        <div className={styles.label}>비고 <span>(관리자만, 모델에 안 보냄)</span></div>
                                        <input className={styles.input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="예: 11월 본사 공지 반영" />
                                    </div>
                                    <div className={styles.field}>
                                        <div className={styles.label}>출처</div>
                                        <input className={styles.input} value={source} onChange={(e) => setSource(e.target.value)} placeholder="예: 채팅로그 / 실장 컨펌" />
                                    </div>
                                </div>

                                {
                                    isEdit && item ?
                                        <div className={styles.meta}>
                                            마지막 수정 {formatFullDateTime(item.updated_at)}{item.updated_by ? ` · ${item.updated_by}` : ""} · 등록 {formatFullDateTime(item.created_at)}{item.created_by ? ` · ${item.created_by}` : ""}
                                            <br />
                                            이 항목에 들어온 미처리 피드백 {feedbackCount}건
                                            {feedbackCount > 0 ? <> (<button className={styles.linkBtn} style={{ padding : 0, fontSize : "12px" }} onClick={props.goReview}>검토 큐에서 보기</button>)</> : null}
                                            {item.freq ? ` · 채팅로그 빈도 ${item.freq}` : ""}
                                        </div>
                                        : null
                                }
                            </>
                    }
                </div>

                <div className={styles.drawerFooter}>
                    <div>
                        {isEdit && item && item.status === "active" ? <button className={`${styles.btn} ${styles.btnDanger}`} onClick={() => setConfirmDelete(true)}>삭제</button> : null}
                    </div>
                    <div className={styles.rowFlex}>
                        <button className={styles.btn} onClick={props.onClose} disabled={saving}>취소</button>
                        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={save} disabled={saving || loading} style={{ padding : "0 20px" }}>
                            {saving ? "저장 중…" : "저장 (배포 전까지 미반영)"}
                        </button>
                    </div>
                </div>

                <FaqCategoryModal
                    open={catModalOpen}
                    onClose={() => setCatModalOpen(false)}
                    categories={props.categories}
                    onChanged={props.onCategoriesChanged}
                    onCreated={(name : string) => setCategory(name)}
                />

                <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)}>
                    <ModalDialog sx={{ padding : 0, borderRadius : "14px", width : "380px", maxWidth : "95vw" }}>
                        <div className={styles.modal} style={{ boxShadow : "none" }}>
                            <div className={styles.modalHeader}>FAQ 삭제</div>
                            <div className={styles.modalBody}>
                                <div style={{ fontSize : "14px", lineHeight : 1.6 }}>
                                    <b>#{props.itemId}</b> 을(를) 삭제할까요?
                                    <div className={styles.helper} style={{ marginTop : "8px" }}>삭제됨 상태로 바뀌고(복구 가능), 다음 배포부터 직원 화면에서 빠집니다.</div>
                                </div>
                            </div>
                            <div className={styles.modalFooter}>
                                <button className={styles.btn} onClick={() => setConfirmDelete(false)}>취소</button>
                                <button className={`${styles.btn} ${styles.btnDanger}`} onClick={remove}>삭제</button>
                            </div>
                        </div>
                    </ModalDialog>
                </Modal>
            </div>
        </SwipeableDrawer>
    );
};

export default FaqEditDrawer;
